import React, { useState } from "react";
import { Heading } from "components/Heading";
import RfxResponseComp from "newui/features/Rfx/Submission/components/RfxSubmissionStatusPill/RfxResponseComp";
import moment from "moment";
import { SkeletonCircle } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { Divider } from "components/Divider";
import { Icon } from "assets/Icon";
import {
	LONG_DATE_TIME_FORMAT,
	SubmissionDeadline,
} from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import RFQStatusComp from "components/RFQ/RFQStatusComp";
import EmailLink from "newui/features/Rfx/components/EmailLink";
import Tooltip from "components/Tooltip";
import { Avatar } from "components/RFQ/Avatar";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { ListHorizontalItem } from "../ListHorizontalItem";
import { isDev } from "utils/logging";
import AccordionLargeTextDisplay from "newui/components/AccordionLargeTextDisplay";

interface RfxDetailsProps {
	rfxData?: Rfx;
	isLoading?: boolean;
	isTemplate?: boolean;
	submissionStatus?: string;
	submissionStatusDate?: string;
	onReload?: () => void;
}

export const RfxDetails: React.FC<RfxDetailsProps> = ({
	rfxData,
	isLoading = false,
	isTemplate = false,
	submissionStatus,
	submissionStatusDate,
	onReload,
}) => {
	const projectName = rfxData?.project?.name;
	const [isDescriptionShown, setIsDescriptionShown] = useState(false);

	const headerDetail = (
		<div className="mb-2 flex w-full items-end justify-start">
			<div className="pb-1">
				{isLoading ? (
					<SkeletonCircle
						width={12}
						height={12}
						startColor="lightgray"
					/>
				) : (
					// @ts-ignore
					<Avatar
						avatar={rfxData?.project?.logoPath}
						defaultImage="company"
						size="lg"
					/>
				)}
			</div>

			<div className="flex flex-grow flex-wrap items-center justify-between px-4 !leading-[50px]">
				<div>
					{!isLoading ? (
						<>
							{projectName && (
								<p className="text-base text-secondary-500">{projectName}</p>
							)}
							<Heading fontSize="3rem">
								<div className="text-royal-blue">{rfxData?.name}</div>
							</Heading>
						</>
					) : (
						<SkeletonBase
							width="300px"
							height="36px"
						/>
					)}
				</div>
				{!isTemplate && (
					<div>
						{!isLoading ? (
							<div style={{ float: "right" }}>
								<RfxResponseComp
									response={
										submissionStatus ? submissionStatus?.toUpperCase() : ""
									}
									responseDate={submissionStatusDate}
									size="p-4"
								/>
							</div>
						) : (
							<SkeletonBase
								width="100px"
								height="32px"
								className="!rounded-full"
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);

	const previewLink = !isLoading && (
		<div className="mt-3 w-full">
			<Divider />
			<div className="mt-4">
				<a
					href="#rfx-container"
					className="mt-5"
				>
					<div
						className="font-mediumborder-[#B0CEFF] flex items-center justify-center rounded-full border border-solid bg-utility-blue-light-50 p-2 text-center text-utility-brand-500"
						style={{ width: "fit-content", fontSize: "14px" }}
					>
						<div className="flex items-center justify-center rounded-full border border-solid border-[#B0CEFF] bg-white pb-1 pl-3 pr-3 pt-1">
							<div>
								<Icon icon="arrow-down" />
							</div>
							<div className="ml-2">Preview RFx below</div>
						</div>
					</div>
				</a>
			</div>
		</div>
	);

	if (isTemplate) {
		return (
			<div className="relative min-h-[200px] rounded-lg border border-solid border-[#EAECF0] bg-white p-10 shadow-lg">
				{headerDetail}
				{previewLink}
			</div>
		);
	}

	return (
		<div className="relative min-h-[430px] rounded-lg border border-solid border-[#EAECF0] bg-white p-10 shadow-lg">
			{!isLoading && onReload && isDev() && (
				<div className="absolute right-4 top-4 z-50">
					<Tooltip
						label="Reload data"
						placement="top"
						portalProps={{ appendToParentPortal: true }}
					>
						<Icon
							icon="refresh"
							className="cursor-pointer text-gray-500 hover:text-gray-700"
							onClick={onReload}
						/>
					</Tooltip>
				</div>
			)}

			{/* Header */}
			{headerDetail}

			<AccordionLargeTextDisplay
				isLoading={isLoading}
				text={rfxData?.description}
				title={"Project Description"}
			/>
			<AccordionLargeTextDisplay
				isLoading={isLoading}
				text={rfxData?.escopeOfService}
				title={"Scope of Service"}
			/>

			{/* Details */}
			<div className="flex flex-col gap-5">
				{/* Metadata */}
				<div className="flex flex-1 flex-wrap justify-between border-t border-solid border-[#EAECF0] pt-3">
					<div className="grid gap-x-4 gap-y-3 md:grid-cols-1 lg:grid-cols-2">
						{!isLoading ? (
							<>
								<ListHorizontalItem
									title="Project Status"
									value={<RFQStatusComp status={rfxData?.project?.status} />}
								/>
								<ListHorizontalItem
									title="RFx ID"
									value={rfxData?.rfqIdNumber ?? "-"}
								/>
								<ListHorizontalItem
									title="Request Type"
									value={rfxData?.rfxType?.name ?? rfxData?.type ?? "-"}
								/>
								<ListHorizontalItem
									title="Client"
									value={rfxData?.client ?? "-"}
								/>
								<ListHorizontalItem
									title="RFx Location"
									value={rfxData?.location ?? "-"}
								/>
								<ListHorizontalItem
									title="Project"
									value={rfxData?.project?.name ?? "-"}
								/>
								<ListHorizontalItem
									title="RFx Published"
									value={
										rfxData?.rfxPublished
											? moment(rfxData?.rfxPublished).format(
													"dddd, D MMMM YYYY"
												)
											: "-"
									}
								/>
								<ListHorizontalItem
									title="Submission Deadline"
									value={
										<SubmissionDeadline
											deadlineDateTime={new Date(rfxData?.submissionDeadline)}
											submissionStatus={submissionStatus}
											deadlineTimeZone={rfxData?.submissionTimezone}
											dateFormat={LONG_DATE_TIME_FORMAT}
											twoLines={false}
										/>
									}
								/>
								<ListHorizontalItem
									title="Submitted On"
									value={
										submissionStatusDate
											? moment(submissionStatusDate).format(
													LONG_DATE_TIME_FORMAT
												)
											: "Not Submitted"
									}
								/>
							</>
						) : (
							Array.from({ length: 5 }).map((_, i) => (
								<div
									key={i}
									className="flex gap-3"
								>
									<SkeletonBase
										width="120px"
										className="mr-10"
									/>
									<SkeletonBase width="200px" />
								</div>
							))
						)}
					</div>

					{/* RFx Contact */}
					{isLoading ? (
						Array.from({ length: 5 }).map((_, i) => (
							<div
								key={i}
								className="flex gap-3"
							>
								<SkeletonBase
									width="120px"
									className="mr-10"
								/>
								<SkeletonBase width="200px" />
							</div>
						))
					) : (
						<div className="mt-4 rounded-md border border-gray-200 p-2">
							<div className="flex gap-2 border-b border-gray-200 py-1 font-bold">
								<Icon icon="user-03" /> <p>RFx Contact Person</p>
							</div>
							<div className="grid gap-y-3 py-4 md:grid-cols-1 lg:grid-cols-1">
								<ListHorizontalItem
									title="Name"
									value={rfxData?.rfxLead?.fullName ?? "-"}
								/>
								<ListHorizontalItem
									title="Email"
									value={
										rfxData?.rfxLead?.email ? (
											<EmailLink
												email={rfxData?.rfxLead?.email}
												subject={`RE: ${rfxData?.type} ${rfxData?.name}`}
											/>
										) : (
											"-"
										)
									}
								/>
								<ListHorizontalItem
									title="Phone Number"
									value={rfxData?.rfxLead?.phoneNumber ?? "-"}
								/>
							</div>
						</div>
					)}

					{/* Preview Link */}
					{previewLink}
				</div>
			</div>
		</div>
	);
};
